'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import {
    Alert,
    Align,
    Box,
    BoxContent,
    Column,
    Columns,
    Image,
    Row,
    Rows,
    StatusState,
    Title,
    TitleLevels,
    TitleMarkup,
} from '@bytel/trilogy-react';

import { ConfigType } from '@app-types/api';
import { FaiProduct, RangesNewGeneration } from '@app-types/fai';
import { JOURNEYS, RepeaterProduct } from '@app-types/product';

import { useHome } from '@landing/hooks';

import { BAndyouPrice } from '../plan-header/b-and-you-price';
import BboxCard from './bbox-card';
import BboxOtisOption from './bbox-otis-option';
import BboxOtisOptionModal from './bbox-otis-option-modal';
import FaiDetailsModal from './modal-otis';
import OfferRadioList from './offer-radio-list';

interface BoxConfiguratorProps {
    faiProducts: FaiProduct[];
    repeaters?: { [key: string]: RepeaterProduct };
    isLoading: boolean;
    config?: ConfigType;
}

export function BboxConfigurator({ faiProducts, repeaters, isLoading, config }: Readonly<BoxConfiguratorProps>) {
    const [detailModalOffer, setDetailModalOffer] = useState<RangesNewGeneration | undefined>();
    const [isOptionModalOpened, setIsOptionModalOpened] = useState(false);
    const [shouldShowAlert, setShouldShowAlert] = useState(false);
    const {
        setSelectedFaiOption,
        selectedFaiOption,
        selectedFaiProduct: _selectedFaiProduct,
        setSelectedFaiProduct,
        temporarilySelectedFaiProduct: _temporarilySelectedFaiProduct,
        setTemporarilySelectedFaiProduct,
        otisConfigurator,
    } = useHome();
    const searchParams = useSearchParams();

    const isFaiFirst = otisConfigurator.value === JOURNEYS.FAI;

    const selectedFaiProduct = isFaiFirst ? (_selectedFaiProduct ?? faiProducts[0]) : _selectedFaiProduct;
    const temporarilySelectedFaiProduct = _temporarilySelectedFaiProduct ?? faiProducts[0];
    const baseProduct = selectedFaiProduct ?? temporarilySelectedFaiProduct;
    const repeater = baseProduct && repeaters ? repeaters[baseProduct.gencode_offre] : undefined;

    const handleModal = (rangeNg: RangesNewGeneration) => {
        setDetailModalOffer(rangeNg);
    };

    const onChangeOffer = (product: FaiProduct) => {
        if (isFaiFirst) {
            setSelectedFaiProduct(product);
            setTemporarilySelectedFaiProduct(null);
        } else {
            setSelectedFaiProduct(null);
            setTemporarilySelectedFaiProduct(product);
        }
        if (selectedFaiOption && product.range_ng !== RangesNewGeneration.VERY_FIT) {
            setSelectedFaiOption('');
        }
    };

    const onAddOtisRepeater = () => {
        if (!repeater) {
            return;
        }
        if (selectedFaiProduct?.range_ng !== RangesNewGeneration.VERY_FIT) {
            setShouldShowAlert(true);
            setTimeout(() => {
                setShouldShowAlert(false);
            }, 5000);
            return;
        }
        if (selectedFaiOption) {
            setSelectedFaiOption('');
        } else {
            setSelectedFaiOption(repeater.gencode);
        }
    };

    useEffect(() => {
        if (!faiProducts[0]) {
            return;
        }

        const rangeNg = searchParams.get('range_ng');
        const commercialSeries = searchParams.get('commercial_series');
        const faiProductFromParams =
            rangeNg && commercialSeries
                ? faiProducts.find(
                      (offer) => offer.range_ng === rangeNg && offer.commercial_series === commercialSeries,
                  )
                : undefined;

        if (faiProductFromParams) {
            setSelectedFaiProduct(faiProductFromParams);
            setTemporarilySelectedFaiProduct(faiProductFromParams);
            return;
        }

        if (isFaiFirst) {
            setSelectedFaiProduct(faiProducts[0]);
            return;
        }

        setTemporarilySelectedFaiProduct(faiProducts[0]);
    }, [faiProducts, isFaiFirst]);

    const shouldShowByouAlert = !!(
        isFaiFirst &&
        baseProduct?.alerte_client_byou &&
        baseProduct.title_alert &&
        baseProduct.text_alert
    );

    if (!faiProducts || faiProducts.length === 0) {
        return;
    }

    return (
        <Rows gap={8} className='h-fit'>
            <Row narrow>
                <Box data-testid='bbox-container' id='bbox-configurator' shadowless>
                    <BAndyouPrice
                        isFai
                        isLoading={isLoading}
                        baseProduct={baseProduct}
                        icon={
                            !isLoading && baseProduct?.picto?.public_url ? (
                                <Image src={baseProduct.picto.public_url} alt='' width={80} />
                            ) : null
                        }
                        config={config}
                    />
                    <BoxContent>
                        <Title level={TitleLevels.FIVE} markup={TitleMarkup.H2}>
                            Quelle offre voulez-vous&nbsp;?
                        </Title>
                        <OfferRadioList
                            faiProducts={faiProducts}
                            selectedOffer={baseProduct}
                            onChangeOffer={onChangeOffer}
                        />
                        <Columns align={Align.CENTER}>
                            <Column size={8}>
                                <FaiDetailsModal
                                    faiOffer={baseProduct}
                                    onClose={() => setDetailModalOffer(undefined)}
                                    active={!!detailModalOffer}
                                />
                                <BboxCard selectedOffer={baseProduct} onClickDetails={handleModal} />
                            </Column>
                        </Columns>
                    </BoxContent>
                </Box>
                {shouldShowByouAlert && (
                    <Alert
                        status={StatusState.INFO}
                        description={baseProduct.text_alert}
                        display
                        title={baseProduct.title_alert}
                    />
                )}
            </Row>

            {repeater && (
                <Row narrow>
                    <BboxOtisOptionModal
                        isRepeaterAdded={selectedFaiOption === repeater.gencode}
                        onAddOtisRepeater={onAddOtisRepeater}
                        onClose={() => setIsOptionModalOpened(false)}
                        active={isOptionModalOpened}
                        shouldShowAlert={shouldShowAlert}
                        repeater={repeater}
                    />
                    <BboxOtisOption
                        onAddOtisRepeater={onAddOtisRepeater}
                        onClick={() => setIsOptionModalOpened(true)}
                        shouldShowAlert={shouldShowAlert}
                        repeater={repeater}
                    />
                </Row>
            )}
        </Rows>
    );
}
